import moment from 'moment';
import { Fragment } from 'react';
import { Letter, Quality as ImageQuality } from '@abhisek507/html2multipagepdf/constants';
import { DEFAULT_FORMFIELD_AXENA } from './cmnTemplateForAxena.constant';
import { DEFAULT_FORMFIELD_DEXCOM } from './cmnTemplateForDexcom.constant';
import { DEFAULT_FORMFIELD_APRETUDE } from './cmnTemplateForApretude.constant';
import { DEFAULT_FORMFIELD_VYEPTI } from './cmnTemplateForVyepti.constant';

export const DateFormat = 'YYYY-MM-DD';
export const MonthAndYearOnlyFormat = 'YYYY-MM';
export const MaxDate = moment().format(DateFormat);
export const MaxMonthAndYear = moment(MaxDate).format(MonthAndYearOnlyFormat);
export const CheckBoxChecked = {
  yes: 'yes',
  no: 'no'
};
export const HypoglycemiaUnawarenessSymptoms = [
  'At times, I do not experience symptoms when my blood sugar drops',
  'I have recurring episodes of severe hypoglycemia'
];
export const InputInjectInhaleIdsAnswerMap = new Map([
  ['0 time per day', '0'],
  ['0 times per day', '0'],
  ['1 time per day', '1'],
  ['1 times per day', '1'],
  ['2 time per day', '2'],
  ['2 times per day', '2'],
  ['3 time per day', '3'],
  ['3 times per day', '3'],
  ['4 time per day', '4'],
  ['4 times per day', '4'],
  ['5 time per day', '5'],
  ['5 times per day', '5'],
  ['more than 6 time per day', '6+'],
  ['more than 6 times per day', '6+'],
]);
export const ProvidersAndCreatePDF = 'ProvidersAndCreatePDF';
export const PID_ProvidersAndCreatePDF = 'PID_ProvidersAndCreatePDF';
export const QID_PumpCheckbox = 'QID_PumpCheckbox';
export const QID_InjectInhaleCheckboxes = 'QID_InjectInhaleCheckboxes';
export const QID_InjectInhaleInputs = 'QID_InjectInhaleInputs';
export const QID_HbA1CInput = 'QID_HbA1CInput';
export const QID_HbA1CDate = 'QID_HbA1CDate';
export const QID_SMBGInput = 'QID_SMBGInput';
export const QID_CGMInput = 'QID_CGMInput';
export const QID_HypoglycemiaUnawarenessSymptomsCheckboxes = 'QID_HypoglycemiaUnawarenessSymptomsCheckboxes';
export const QID_CGM = 'QID_CGM';
export const QID_FrequentAdjustmentsInTreatment = 'QID_FrequentAdjustmentsInTreatment';
export const FAX_ProvidersAndCreatePDF = 'FAX_ProvidersAndCreatePDF';
export const Email_ProvidersAndCreatePDF = 'Email_ProvidersAndCreatePDF';

export const DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING = {
  sentTo: 'Ascensia Diabetes Care',
  prescriberAddress: '7033 E Greenway Parkway, Suite 310',
  prescriberState: 'Arizona',
  prescriberZip: '85254',
  prescriberCity: 'Scottsdale',
  prescriberPhone: '(480) 707-4549',
  prescriberFax: '(833) 701-6984',
};

export const DEFAULT_PDF_INFO = () => ({
  patientInfo: {
    dob: '',
    lastName: '',
    firstName: '',
    address: '',
    state: '',
    zip: '',
    city: '',
    phone: '',
    email: '',
  },
  prescriberInfo: {
    preName: '',
    firstName: '',
    lastName: '',
    npiNo: '',
  },
  provider: {
    signature: {
      firstName: '',
      lastNmae: '',
      prifix: ''
    },
  }
});

export const DEFAULT_FORMFIELD = {
  sentTo: DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING.sentTo,
  sentDate: MaxDate,

  patientDob: '',
  patientFullName: '',
  patientAddress: '',
  patientState: '',
  patientZip: '',
  patientCity: '',
  patientPhone: '',
  patientInsuranceCompany: '',
  patientInsuranceNo: '',

  prescriberPreName: '',
  prescriberFullName: '',
  prescriberNpi: '',
  prescriberAddress: DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING.prescriberAddress,
  prescriberState: DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING.prescriberState,
  prescriberZip: DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING.prescriberZip,
  prescriberCity: DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING.prescriberCity,
  prescriberPhone: DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING.prescriberPhone,
  prescriberFax: DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING.prescriberFax,

  icd10Diagnosis: '',
  injectPerDay: '',
  inhalePerDay: '',
  hbA1c: '',
  hbA1cDate: '',
  smbg: '',
  cgm: '',

  lastVisitDate: '',
  followUpRoutine: '3-6 months',

  'checkbox-ur': false,
  urgentReview: '',

  hcpSignature: '',
  signatureDate: '',

  'checkbox-0446T': true,
  'checkbox-0447T': true,
  'checkbox-0448T': true,
  'checkbox-A9276': true,
  'checkbox-A9277': true,

  'checkbox-iat-pump': false,
  'checkbox-iat-inject': false,
  'checkbox-iat-inhale': false,

  'checkbox-hhu': CheckBoxChecked.no, // 'yes' or 'no'
  'checkbox-icgm': CheckBoxChecked.no, // 'yes' or 'no'
  'checkbox-lticgm': CheckBoxChecked.yes, // 'yes' or 'no'
  'checkbox-faitr': CheckBoxChecked.no, // 'yes' or 'no'
  'checkbox-moti': CheckBoxChecked.yes, // 'yes' or 'no'

  // inputs for axena ui template
  ...DEFAULT_FORMFIELD_AXENA,
  ...DEFAULT_FORMFIELD_DEXCOM,
  ...DEFAULT_FORMFIELD_APRETUDE,
  ...DEFAULT_FORMFIELD_VYEPTI,
};

export const CMN_DATA = {
  orderInfo: {
    questions: [
      {
        questionId: '0446T',
        label: 'Creation of subcutaneous pocket with insertion of implantable interstitial glucose sensor, including system activation and patient training. (Initial insertion, 1 sensor included, performed and billed by health care provider, HCP)',
        isModificationAllowed: true,
        isChecked: true
      },
      {
        questionId: '0447T',
        label: 'Removal of implantable interstitial glucose sensor from subcutaneous pocket via incision. (Billed by HCP)',
        isModificationAllowed: false,
        isChecked: false
      },
      {
        questionId: '0448T',
        label: 'Removal of implantable interstitial glucose sensor with creation of subcutaneous pocket at different anatomic site, insertion of new implantable sensor, including system activation. (Includes 1 sensor, performed and billed by HCP)',
        isModificationAllowed: true,
        isChecked: false
      },
      {
        questionId: 'A9276',
        label: 'Sensor; invasive (e.g., subcutaneous), disposable, for use with interstitial continuous glucose monitoring system, 1 unit = 1 day supply',
        isModificationAllowed: false,
        isChecked: true
      },
      {
        questionId: 'A9277',
        label: 'Transmitter; external, for use with interstitial continuous glucose monitoring system',
        isModificationAllowed: false,
        isChecked: true
      }
    ]
  },
  patientInfo: {
    firstName: 'Abhisek',
    lastName: 'Dutta',
    dob: '07/18/1996',
    address: '43/31, Behala, Kolkata',
    state: 'West Bengal',
    zip: 700104,
    city: 'Kolkata',
    phone: '9876543210',
    insuranceCompany: 'SBI',
    insuranceNo: 54321
  },
  prescriberInfo: {
    firstName: 'Abhisek',
    lastName: 'Dutta',
    address: '43/31, Behala, Kolkata',
    state: 'West Bengal',
    zip: 700104,
    city: 'Kolkata',
    phone: '9876543210',
    fax: '8765432100',
    npiNo: 78901
  },
  medicalNecessity: {},
  priorAuthorizationReview: {},
  provider: {
    attestation: '',
    signature: ''
  }
};

export const ITEMPLATE = ({
  Template = Fragment,
  id = '',
  fileName = '',
  preparePDF = async () => {},
  enabled = false,
  requiredFields = [],
  hasPIDDependency = false,
  pageOptions = Letter,
  imageQuality = ImageQuality[100],
}) => ({
  Template,
  id,
  fileName,
  preparePDF,
  enabled,
  requiredFields,
  hasPIDDependency,
  pageOptions,
  imageQuality,
});
